import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'Sarah Jenkins',
      location: 'Gaithersburg, MD',
      image:
        'https://images.unsplash.com/photo-1613394242132-1268854bde44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMGhhcHB5JTIwaG9tZW93bmVyJTIwc21pbGUlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzcyNzgyODA4fDA&ixlib=rb-4.1.0&q=85',
      quote:
        "Our bathroom renovation exceeded all expectations. Cristian and his team were professional, on time, and the quality of work was outstanding. The attention to detail in the tile work and fixtures made our bathroom feel like a luxury spa.",
      project: 'Bathroom Renovation',
      rating: 5,
    },
    {
      name: 'Michael Ross',
      location: 'Rockville, MD',
      image:
        'https://images.pexels.com/photos/4883435/pexels-photo-4883435.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      quote:
        "Cristian's communication throughout our kitchen remodel was exceptional. He kept us informed every step of the way and delivered exactly what was promised. The transformation of our kitchen has completely changed how we use our home.",
      project: 'Kitchen Remodel',
      rating: 5,
    },
    {
      name: 'Emily Chen',
      location: 'Bethesda, MD',
      image:
        'https://images.unsplash.com/photo-1562337404-3044c84ac061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxwb3J0cmFpdCUyMGhhcHB5JTIwaG9tZW93bmVyJTIwc21pbGUlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzcyNzgyODA4fDA&ixlib=rb-4.1.0&q=85',
      quote:
        "We hired Lux Renovations for a complete home renovation and couldn't be happier. From the initial consultation to the final walkthrough, the team was professional, courteous, and delivered stunning results. Highly recommend!",
      project: 'Full Home Renovation',
      rating: 5,
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      ref={ref}
      className="section-padding bg-[#0A0A0A] relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
            Testimonials
          </p>
          <h2
            data-testid="testimonials-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-6"
          >
            What Our Clients Say
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative">
            {/* Quote Icon */}
            <Quote className="absolute -top-4 left-0 md:left-8 w-16 h-16 text-[#D4AF37]/20" />

            {/* Testimonial Content */}
            <div className="text-center px-4 md:px-16 py-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.name}
                  data-testid={`testimonial-${index}`}
                  className={`transition-all duration-500 ${
                    currentIndex === index
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-8'
                  }`}
                >
                  {/* Stars */}
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-[#D4AF37] fill-current"
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="font-serif text-xl md:text-2xl text-white/90 leading-relaxed mb-8 italic">
                    "{testimonial.quote}"
                  </p>

                  {/* Author */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37] mb-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-white font-semibold text-lg">
                      {testimonial.name}
                    </p>
                    <p className="text-[#D4AF37] text-sm">
                      {testimonial.project}
                    </p>
                    <p className="text-white/50 text-xs mt-1">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-center items-center gap-4 mt-8">
              <Button
                onClick={prevTestimonial}
                data-testid="testimonial-prev"
                variant="outline"
                size="icon"
                className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:border-[#D4AF37] rounded-none"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              {/* Dots */}
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    data-testid={`testimonial-dot-${index}`}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentIndex === index
                        ? 'bg-[#D4AF37] w-8'
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={nextTestimonial}
                data-testid="testimonial-next"
                variant="outline"
                size="icon"
                className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:border-[#D4AF37] rounded-none"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
